import { useRef, useCallback, useEffect } from "react"

/**
 * Auto-hide scrollbar — matches v1 behavior.
 *
 * Starts hidden (`.no-scrollbar` class on element).
 * On scroll: removes `.no-scrollbar` (thumb appears).
 * After 2s idle: re-adds `.no-scrollbar` (thumb hides).
 *
 * Usage:
 * ```tsx
 * const { onScroll, ref } = useScrollbarAutoHide<HTMLDivElement>()
 * <div ref={ref} onScroll={onScroll} className="no-scrollbar overflow-y-auto">
 * ```
 *
 * Or with an external ref:
 * ```tsx
 * const { onScroll } = useScrollbarAutoHide({ externalRef: scrollRef })
 * ```
 */

const HIDE_DELAY = 2000

interface UseScrollbarAutoHideOptions {
  /** External ref to attach to instead of creating a new one */
  externalRef?: React.RefObject<HTMLElement | null>
  /** Delay in ms before hiding scrollbar after scroll stops */
  delay?: number
}

export function useScrollbarAutoHide<T extends HTMLElement = HTMLElement>(
  options?: UseScrollbarAutoHideOptions,
) {
  const internalRef = useRef<T | null>(null)
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const delay = options?.delay ?? HIDE_DELAY

  const getEl = useCallback((): HTMLElement | null => {
    return options?.externalRef?.current ?? internalRef.current
  }, [options?.externalRef])

  const onScroll = useCallback(() => {
    const el = getEl()
    if (!el) return

    if (timeout.current) clearTimeout(timeout.current)

    if (el.classList.contains("no-scrollbar")) {
      el.classList.remove("no-scrollbar")
    }

    timeout.current = setTimeout(() => {
      el.classList.add("no-scrollbar")
    }, delay)
  }, [getEl, delay])

  // Ensure initial class and cleanup
  useEffect(() => {
    const el = getEl()
    if (el && !el.classList.contains("no-scrollbar")) {
      el.classList.add("no-scrollbar")
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [getEl])

  return { ref: internalRef as React.RefObject<T>, onScroll }
}
